import { getEnvVariable } from '@/utils/get-env-variable';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiRequestOptions extends RequestInit {
  headers?: Record<string, string>;
  parseAs?: 'json' | 'text' | 'blob';
}

export interface ApiError extends Error {
  status: number;
  info?: unknown;
}

export class ApiClient {
  private baseUrl: string;
  private headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
  }

  public initilize(options: { token?: string }) {
    this.setHeader('Authorization', `Bearer ${options.token ?? ''}`);
  }

  public setHeader(key: string, value: string | null) {
    if (value) this.headers[key] = value;
    else delete this.headers[key];
  }

  public setHeaders(newHeaders: Record<string, string>) {
    this.headers = { ...this.headers, ...newHeaders };
  }

  public async request<T>(endpoint: string, method: HttpMethod = 'GET', options: ApiRequestOptions = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const { headers: optionsHeaders, ...restOptions } = options;
    const headers = { ...this.headers, ...(optionsHeaders || {}) };

    try {
      const response = await fetch(url, {
        ...restOptions,
        method,
        headers,
        body: options.body,
        credentials: 'include',
        mode: 'cors',
      });

      if (!response.ok) {
        const error: ApiError = new Error(`HTTP ${response.status}`) as ApiError;
        error.status = response.status;
        try {
          error.info = await response.json();
        } catch {
          error.info = await response.text();
        }
        throw error;
      }

      switch (options.parseAs) {
        case 'text':
          return (await response.text()) as T;
        case 'blob':
          return (await response.blob()) as T;
        default:
          return (await response.json()) as T;
      }
    } catch (error) {
      console.error('API Request failed:', {
        url,
        method,
        error: error instanceof Error ? error.message : error,
        baseUrl: this.baseUrl,
        endpoint,
      });
      throw error;
    }
  }

  public get<T>(endpoint: string, options?: ApiRequestOptions) {
    return this.request<T>(endpoint, 'GET', options);
  }

  public post<T>(endpoint: string, body?: Record<string, unknown>, options?: ApiRequestOptions) {
    return this.request<T>(endpoint, 'POST', {
      ...options,
      body: JSON.stringify(body),
    });
  }

  public put<T>(endpoint: string, body?: unknown, options?: ApiRequestOptions) {
    return this.request<T>(endpoint, 'PUT', {
      ...options,
      body: JSON.stringify(body),
    });
  }

  public delete<T>(endpoint: string, options?: ApiRequestOptions) {
    return this.request<T>(endpoint, 'DELETE', options);
  }
}

export const apiClient = new ApiClient(getEnvVariable('VITE_API_URL', 'https://api.appcharge.com'));
